import React, { useEffect, useState } from "react";
import { listDonations, deleteDonation, updateDonation, withBase } from "./api";
import styles from "./List.module.css";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Verified': return '#16a34a';
    case 'Rejected': return '#ef4444';
    default: return '#f59e0b';
  }
};

export default function DonationsList() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: 'Pending', notes: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  useEffect(() => {
    listDonations()
      .then((res) => setItems(res.items || []))
      .catch((e) => setError(e.message || "Failed to load"));
  }, []);

  const handleStatusChange = async () => {
    if (!selectedDonation) return;
    
    try {
      await updateDonation(selectedDonation.id, statusUpdate.status, statusUpdate.notes);
      setItems(items.map(item =>
        item.id === selectedDonation.id
          ? { ...item, status: statusUpdate.status, notes: statusUpdate.notes }
          : item
      ));
      setSelectedDonation(null);
      setStatusUpdate({ status: 'Pending', notes: '' });
    } catch (e) {
      setError(e.message || 'Update failed');
    }
  };

  const paginatedItems = items.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(items.length / pageSize);

  return (
    <div className={styles.card}>
      <div className={styles.headerRow}>
        <div className={styles.title}>Donations</div>
      </div>
      {error && <div className={styles.empty}>{error}</div>}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.th}>#</th>
            <th className={styles.th}>Name</th>
            <th className={styles.th}>Email</th>
            <th className={styles.th}>Phone</th>
            <th className={styles.th}>Amount</th>
            <th className={styles.th}>Mode</th>
            <th className={styles.th}>Status</th>
            <th className={styles.th}>Date</th>
            <th className={styles.th}>Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedItems.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={9}>No donations found.</td>
            </tr>
          ) : (
            paginatedItems.map((it, idx) => (
              <tr key={it.id}>
                <td className={styles.td}>{(currentPage - 1) * pageSize + idx + 1}</td>
                <td className={styles.td}>{it.full_name}</td>
                <td className={styles.td} style={{ fontSize: '0.9rem' }}>{it.email}</td>
                <td className={styles.td}>{it.phone}</td>
                <td className={styles.td} style={{ fontWeight: '600', color: '#16a34a' }}>
                  {formatCurrency(it.donation_amount)}
                </td>
                <td className={styles.td}>{it.donation_mode}</td>
                <td className={styles.td}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: '999px',
                    backgroundColor: getStatusColor(it.status),
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {it.status}
                  </span>
                </td>
                <td className={styles.td} style={{ fontSize: '0.9rem' }}>{formatDate(it.created_at)}</td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    <button
                      className={`${styles.btn} ${styles.btnEdit}`}
                      onClick={() => {
                        setSelectedDonation(it);
                        setStatusUpdate({ status: it.status, notes: it.notes || '' });
                      }}
                    >
                      View/Edit
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnDelete}`}
                      disabled={busyId === it.id}
                      onClick={async () => {
                        setBusyId(it.id);
                        try {
                          await deleteDonation(it.id);
                          setItems(items.filter(x => x.id !== it.id));
                        } catch (e) {
                          setError(e.message || 'Delete failed');
                        } finally {
                          setBusyId(null);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '1.5rem', padding: '0 1rem' }}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            Previous
          </button>
          <span style={{ alignSelf: 'center', fontWeight: '600' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            Next
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDonation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ marginTop: 0, color: '#111827' }}>Donation Details</h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Full Name</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>{selectedDonation.full_name}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Email</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '0.9rem' }}>{selectedDonation.email}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Phone</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>{selectedDonation.phone}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>PAN Number</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>{selectedDonation.pan_number}</p>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Address</label>
              <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '0.9rem' }}>{selectedDonation.address}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Donation Reason</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '0.9rem' }}>{selectedDonation.donation_reason}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Mode</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>{selectedDonation.donation_mode}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>UTR Number</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px' }}>{selectedDonation.utr_number}</p>
              </div>
              <div>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.3rem' }}>Amount</label>
                <p style={{ margin: 0, padding: '0.5rem 0.75rem', backgroundColor: '#f9fafb', borderRadius: '6px', fontWeight: '600', color: '#16a34a' }}>
                  {formatCurrency(selectedDonation.donation_amount)}
                </p>
              </div>
            </div>

            {selectedDonation.screenshot_path && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.5rem' }}>Payment Screenshot</label>
                <img src={withBase(selectedDonation.screenshot_path)} alt="payment" style={{ maxWidth: '100%', borderRadius: '8px', maxHeight: '300px' }} />
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.5rem' }}>Status</label>
              <select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontFamily: 'inherit'
                }}
              >
                <option value="Pending">Pending</option>
                <option value="Verified">Verified</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ fontWeight: '600', color: '#4b5563', display: 'block', marginBottom: '0.5rem' }}>Admin Notes</label>
              <textarea
                value={statusUpdate.notes}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
                placeholder="Add notes about this donation"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontFamily: 'inherit',
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleStatusChange}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Update Status
              </button>
              <button
                onClick={() => {
                  setSelectedDonation(null);
                  setStatusUpdate({ status: 'Pending', notes: '' });
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#f3f4f6',
                  color: '#111827',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
